import { DayjsValue } from '@issuemanager/apps/client/utils';
import {
  ISSUE_WORKFLOW_EXECUTION_TYPE,
  ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES,
} from '@issuemanager/constants';
import { TFunction } from 'i18next';
import { z } from 'zod';
import { ActionSequentialDueDateValidationArgs } from '../../../issue/schemas/common';

export type GetIssueTemplateFormSchemaArgs = {
  t: TFunction<('issue' | 'action' | 'settings' | 'common')[], undefined>;
  timezoneOffset?: number;
  disablePast: (
    timezoneOffset: number,
    value: DayjsValue,
    maxDate?: DayjsValue,
  ) => boolean;
  SLAs: any[];
  isDateEarlierThanSLA: (SLAs: any[], severity: string, value: any) => boolean;
};

const actionSchema = z.object({
  title: z.string(),
  description: z.string().nullable().optional(),
  dueDate: z
    .any()
    .transform((val) => val ?? '')
    .nullable()
    .optional(),
  ownerId: z.string().nullable().optional(),
  approverId: z.string().nullable().optional(),
  status: z.any(), // Used for carrying additional form information, no type needed
  customId: z.any(), // Used for carrying additional form information, no type needed
});

export const baseSchema = z.object({
  title: z.string().nullish(),
  isAutomatic: z.boolean(),
  issueTypeId: z.string().nullish(),
  issueSubtypeId: z.string().nullish(),
  managerId: z.any().transform((id) => id ?? ''),
  ownerId: z.any().transform((id) => id ?? ''),
  approverId: z.any().transform((id) => id ?? ''),
  severity: z.any().transform((s) => s ?? ''),
  dueDate: z.any().transform((val) => val ?? ''),
  executionType: z.enum(ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES),
  actions: z.array(actionSchema).optional(),
  govmapGroups: z.any(),
});

type ActionSchemaType = z.infer<typeof actionSchema>;

type IssueDueDateDisablePastValidationArgs = Pick<
  GetIssueTemplateFormSchemaArgs,
  'disablePast' | 'timezoneOffset' | 't'
> & { ctx: z.RefinementCtx; dueDate: any };
type IssueDueDateEarlierThanSLAPastValidationArgs = Pick<
  GetIssueTemplateFormSchemaArgs,
  'isDateEarlierThanSLA' | 'SLAs' | 't'
> & { ctx: z.RefinementCtx; data: { dueDate: any; severity?: string | null } };
type IssueDueDateOnlyNumbersValidationArgs = Pick<
  GetIssueTemplateFormSchemaArgs,
  't'
> & { ctx: z.RefinementCtx; dueDate: any };

type ActionDueDateValidationsArgs = Pick<
  GetIssueTemplateFormSchemaArgs,
  'timezoneOffset' | 'SLAs' | 't'
> & {
  ctx: z.RefinementCtx;
  data: {
    dueDate: any;
    action: ActionSchemaType;
    index: number;
    isAutomatic: boolean;
  };
};

export function issueDueDateDisablePastValidation({
  ctx,
  t,
  dueDate,
}: IssueDueDateDisablePastValidationArgs) {
  if (dueDate && Number(dueDate) <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dueDate'],
      message: t(
        'settings:issueTemplates.form.errorMessages.dueDate.disablePast',
      ),
    });
  }
}

export function issueDueDateEarlierThanSLAPastValidation({
  ctx,
  SLAs,
  isDateEarlierThanSLA,
  t,
  data: { dueDate, severity },
}: IssueDueDateEarlierThanSLAPastValidationArgs) {
  if (
    SLAs.length > 0 &&
    severity &&
    !isDateEarlierThanSLA(SLAs, severity, dueDate)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dueDate'],
      message: t(
        'settings:issueTemplates.form.errorMessages.dueDate.earlierOrEqualThanSLA',
      ),
    });
  }
}

export function issueDueDateOnlyNumbersValidation({
  ctx,
  t,
  dueDate,
}: IssueDueDateOnlyNumbersValidationArgs) {
  if (!validateOnlyNumbers(dueDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dueDate'],
      message: t(
        'settings:issueTemplates.form.errorMessages.dueDate.onlyNumbers',
      ),
    });
  }
}

function validateOnlyNumbers(value: any): boolean {
  return !value || /^-?\d+$/.test(value);
}

export function actionDueDateValidations({
  ctx,
  data: { dueDate, action, index, isAutomatic },
  t,
}: ActionDueDateValidationsArgs) {
  if (action.dueDate) {
    // Only numbers validation
    if (!validateOnlyNumbers(action.dueDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t(
          'settings:issueTemplates.form.errorMessages.action.dueDate.onlyNumbers',
        ),
      });
    }
    // Action due date requires issue due date (unless template is not automatic)
    if (
      !(
        !action.dueDate ||
        !!dueDate ||
        (!isAutomatic && action.dueDate && !dueDate)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t(
          'settings:issueTemplates.form.errorMessages.action.dueDate.disableIfNoIssueDueDate',
        ),
      });
    }
    // Past date validation
    if (Number(action.dueDate) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t('action:form.errorMessages.dueDate.disablePast'),
      });
    }
    // Due date must be earlier than the issue due date (only validate if issue has a due date)
    if (
      dueDate &&
      Number(dueDate) > 0 &&
      Number(action.dueDate) >= Number(dueDate)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t('action:form.errorMessages.dueDate.earlierThanIssueDueDate'),
      });
    }
  }
}

export function actionSequentialDueDateValidation({
  data: { executionType, index, actions, action },
  ctx,
  t,
}: ActionSequentialDueDateValidationArgs) {
  if (executionType === ISSUE_WORKFLOW_EXECUTION_TYPE.SEQUENTIAL && index > 0) {
    const prevDueDate = actions?.[index - 1].dueDate;
    if (
      prevDueDate &&
      action.dueDate &&
      Number(action.dueDate) <= Number(prevDueDate)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t(
          'action:form.errorMessages.dueDate.earlierThanPrevActionDueDate',
        ),
      });
    }
  }
}
